import React, { useState, useEffect } from "react";
import { Autocomplete, Box, Button, Grid } from "@mui/material";
import { H4 } from "components/Typography";
import { useTranslation } from "react-i18next";
import AppTextField from "components/input-fields/AppTextField";
import { useFormik } from "formik";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as Yup from "yup"; // component props interface
import XLSX from "sheetjs-style";
import api from "utils/api";
import toast from "react-hot-toast";
import EmployeeDirectory from "components/employee-directory";
import { format } from "date-fns";
import moment from "moment";

const TrainingQuizPerParticipant = ({ quizType }) => {
  const { t } = useTranslation();

  const [employeeDirectoryDialog, setEmployeeDirectoryDialog] = useState(false);

  const initialValues = {
    employee: null,
  };

  const validationSchema = Yup.object({
    employee: Yup.object().required("Employee is Required!"),
  });

  const { handleSubmit, touched, errors, values, setFieldValue } = useFormik({
    initialValues,
    validationSchema: validationSchema,
  });

  // if quizType changes, we need to reset the group input and disable the export button
  useEffect(() => {
    setFieldValue("employee", null);
  }, [quizType]);

  const setCoverData = (coverSheet) => {
    // company name
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.report")}:`,
          t("Reports.training_quiz.per_participant_title"),
        ],
      ],
      { origin: "A1" }
    );

    let currentRow = 3;
    // Employee name
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.training_quiz.employee")}:`,
          `${values.employee?.lastName} ${values.employee?.firstName}`,
        ],
      ],
      { origin: `A${currentRow}` }
    );
    currentRow += 1;
    // Employee ID
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.training_quiz.employee_id")}:`,
          values.employee?.employee_id,
        ],
      ],
      { origin: `A${currentRow}` }
    );

    // --------------------------------------------

    // report generated
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.report_generated")} ${format(
            new Date(),
            "eeee dd MMMM yyyy"
          )}`,
        ],
      ],
      { origin: "G1" }
    );
  };

  const exportToExcel = async () => {
    const employeeId = values.employee?.id || null;

    const result = await api.getTrainingQuizReportByEmployee(employeeId);

    const quizzes = result.data || [];

    if (!quizzes.length) {
      toast.error(t("General.no_data"));
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Cover worksheet
    const coverSheet = XLSX.utils.json_to_sheet([]);
    setCoverData(coverSheet);
    XLSX.utils.book_append_sheet(workbook, coverSheet, t("General.report"));

    // Data worksheet

    // create a new worksheet for each module
    const worksheet = XLSX.utils.json_to_sheet([]);

    const columns = [
      { label: t("Reports.fields.module"), key: "module" },
      { label: t("Reports.fields.quiz_date"), key: "quiz_date" },
      { label: t("Reports.fields.difficulty"), key: "difficulty" },
      { label: t("Reports.fields.module_score"), key: "module_score" },
    ];

    const topics = [];
    // get all topics from all quizzes
    quizzes.forEach((quiz) => {
      quiz.topics?.forEach((topic) => {
        if (!topics.find((t) => t.topicId === topic.topicId)) {
          topics.push(topic);
        }
      });
    });

    // add topics to the columns in the same order
    topics.forEach((topic) => {
      columns.push({ label: topic.topicName, key: topic.topicId });
    });

    const excelData = [];
    // add data for each instructor
    quizzes.forEach((quiz) => {
      const score = (quiz.actual_score / quiz.possible_score) * 100;

      const row = [
        quiz?.moduleName,
        moment(quiz?.createdAt).format("DD/MM/YYYY"),
        t(`Question.fields.difficulty.${quiz?.difficulty}`),
        Math.round(score * 100) / 100 + "%",
      ];

      topics.forEach((topic) => {
        const _topic = quiz.topics?.find((t) => t.topicId === topic.topicId);

        if (_topic) {
          const score =
            ((_topic?.actual_score || 0) / (_topic?.possible_score || 1)) * 100;

          row.push(Math.round(score * 100) / 100 + "%");
        } else {
          row.push("-");
        }
      });

      excelData.push(row);
    });

    XLSX.utils.sheet_add_aoa(worksheet, [
      columns.map((column) => column.label),
    ]);
    XLSX.utils.sheet_add_json(worksheet, excelData, {
      origin: "A2",
      skipHeader: true,
    });

    XLSX.utils.book_append_sheet(workbook, worksheet, t("General.data"));

    // Export Excel file
    const fileName = t("Reports.training_quiz.per_participant_title");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <>
      <H4 pb={2}>{t("Reports.training_quiz.per_participant_title")}</H4>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={1} alignContent="center" alignItems={"center"}>
          <Grid item>
            <Box
              sx={{
                position: "relative",
              }}
            >
              {/* <AppTextField
                fullWidth
                onClick={() => setEmployeeDirectoryDialog(true)}
                name="employee"
                value={values.employee}
                // disabled
                label={t("Reports.fields.group")}
                SelectProps={{
                  IconComponent: KeyboardArrowDown,
                  value: values.group,
                }}
                error={Boolean(touched.group && errors.group)}
                helperText={touched.group && errors.group}
                readOnly
                sx={{
                  position: "relative",
                  width: 300,
                  // outline: "1px solid",
                  // borderColor: "action.disabled",
                  // borderRadius: "8px",
                  cursor: "pointer !important",
                }}
              >
                <MenuItem value={values.employee}>
                  {values.employee}
                </MenuItem>
              </AppTextField> */}
              <Autocomplete
                name="employee"
                value={values.employee || null}
                size="small"
                sx={{ width: 300 }}
                clearOnBlur={false}
                options={[values.employee]}
                getOptionLabel={(option) =>
                  option ? `${option.lastName} ${option.firstName}` : ""
                }
                renderInput={(params) => (
                  <AppTextField
                    {...params}
                    label={t("Reports.training_quiz.employee")}
                    InputProps={{
                      ...params.InputProps,
                      style: { paddingRight: 40 },
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                    error={Boolean(touched.moduleId && errors.moduleId)}
                    helperText={touched.moduleId && errors.moduleId}
                  />
                )}
              />
              <Box
                onClick={() => setEmployeeDirectoryDialog(true)}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "transparent",
                  borderRadius: "8px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    border: "1px solid",
                    borderColor: "action.active",
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
            >
              {t("Reports.export")}
            </Button>
          </Grid>
        </Grid>
      </form>

      <EmployeeDirectory
        open={employeeDirectoryDialog}
        setOpen={setEmployeeDirectoryDialog}
        title={t("TrainingGroup.create.participants.dialog.title")}
        withoutRole={null}
        excludedEmployees={[]}
        handleSubmit={(employeeIds) => {
          if (employeeIds[0]) {
            setFieldValue("employee", employeeIds[0]);
            setEmployeeDirectoryDialog(false);
          }
        }}
        singleSelect
      />
    </>
  );
};

export default TrainingQuizPerParticipant;
